import { useEffect, useRef, useState } from "react";

/**
 * Flags an element once it has scrolled into view, for one-shot entrance
 * animations. The page scrolls inside Layout's container rather than the
 * window, but an observer rooted on the viewport still sees it — ancestors'
 * clipping is taken into account.
 *
 * Without IntersectionObserver, or with a reduced-motion preference, the
 * element is shown straight away rather than left hidden.
 */
export function useReveal({ threshold = 0.15, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return [ref, visible];
}
